import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Settings as SettingsIcon, Sun, Moon, Monitor, Globe, Key,
  Sparkles, CheckCircle2, XCircle, Loader2, Eye, EyeOff, Wifi
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const auth = useAuth();
  const isAdmin = auth?.user?.role === 'admin';
  const [aiConfig, setAiConfig] = useState(null);
  const [keyEdits, setKeyEdits] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [testingModel, setTestingModel] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const fetchAiConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/config`);
      setAiConfig(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchAiConfig(); }, [fetchAiConfig]);

  const handleSaveKey = async (keyName, value) => {
    setSavingKey(keyName);
    try {
      await axios.post(`${API}/ai/keys`, { key_name: keyName, key_value: value });
      toast.success(`Klucz ${keyName} zapisany`);
      setKeyEdits(prev => ({ ...prev, [keyName]: undefined }));
      fetchAiConfig();
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingKey(null); }
  };

  const handleTestModel = async (modelId) => {
    setTestingModel(modelId);
    try {
      const res = await axios.post(`${API}/ai/test-model`, { model_id: modelId });
      if (res.data.success) {
        toast.success(`${modelId}: ${res.data.response?.substring(0, 80)}...`);
      } else {
        toast.error(`${modelId}: ${res.data.error}`);
      }
    } catch (err) { toast.error(t("common.error")); }
    finally { setTestingModel(null); }
  };

  const AI_KEYS = [
    { env: "OPENAI_API_KEY", label: "OpenAI", models: ["gpt-4o"], icon: "O", color: "bg-emerald-500" },
    { env: "ANTHROPIC_API_KEY", label: "Anthropic (Claude)", models: ["claude-sonnet"], icon: "C", color: "bg-amber-500" },
    { env: "XAI_API_KEY", label: "xAI (Grok)", models: ["grok"], icon: "G", color: "bg-blue-500" },
    { env: "GOOGLE_API_KEY", label: "Google (Gemini)", models: ["gemini"], icon: "G", color: "bg-red-500" },
  ];

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">{t("settings.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jezyk */}
        <Card className="rounded-xl border shadow-sm bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              {t("settings.language")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            <Select value={language} onValueChange={(v) => { setLanguage(v); toast.success(t("settings.saved")); }}>
              <SelectTrigger data-testid="settings-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pl">Polski</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Motyw */}
        <Card className="rounded-xl border shadow-sm bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Sun className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              {t("settings.theme")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "light", label: t("settings.themeLight"), icon: Sun },
                { value: "dark", label: t("settings.themeDark"), icon: Moon },
                { value: "system", label: t("settings.themeSystem"), icon: Monitor },
              ].map((opt) => (
                <button key={opt.value} data-testid={`theme-option-${opt.value}`}
                  onClick={() => { setTheme(opt.value); toast.success(t("settings.saved")); }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all duration-200 ${
                    theme === opt.value ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"
                  }`}>
                  <opt.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-xs font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== INTEGRACJE AI (tylko admin) ===== */}
      {isAdmin && (
      <Card className="rounded-xl border shadow-sm bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Key className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            Integracje AI - Klucze API
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <p className="text-xs text-muted-foreground">
            System automatycznie wybiera najlepszy model do kazdego zadania. Dodaj klucze API aby aktywowac modele.
          </p>

          <div className="space-y-3">
            {AI_KEYS.map((keyConfig) => {
              const modelData = aiConfig?.models?.[keyConfig.models[0]];
              const isAvailable = modelData?.available;
              const isEditing = keyEdits[keyConfig.env] !== undefined;
              const keyPreview = modelData?.key_preview;

              return (
                <div key={keyConfig.env} className={`p-4 rounded-lg border transition-all ${
                  isAvailable ? "border-emerald-200 dark:border-emerald-800 bg-card" : "border-border bg-muted/30"
                }`}>
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg ${keyConfig.color} flex items-center justify-center shrink-0`}>
                      <span className="text-white font-bold text-sm">{keyConfig.icon}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-foreground">{keyConfig.label}</h3>
                        {isAvailable ? (
                          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[9px] border-0 rounded-md">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Aktywny
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500 text-[9px] border-0 rounded-md">
                            <XCircle className="w-2.5 h-2.5 mr-0.5" /> Brak klucza
                          </Badge>
                        )}
                      </div>

                      {modelData && (
                        <p className="text-[10px] text-muted-foreground mb-2">{modelData.description}</p>
                      )}

                      {/* Modele */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {keyConfig.models.map(mId => {
                          const m = aiConfig?.models?.[mId];
                          return m ? (
                            <Badge key={mId} variant="outline" className="text-[9px] rounded-md">{m.name}</Badge>
                          ) : null;
                        })}
                        {modelData?.strengths?.slice(0, 3).map((s, i) => (
                          <Badge key={i} variant="secondary" className="text-[8px] rounded-md">{s}</Badge>
                        ))}
                      </div>

                      {/* Key input */}
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <Input type={showKeys[keyConfig.env] ? "text" : "password"}
                              value={keyEdits[keyConfig.env] || ""}
                              onChange={(e) => setKeyEdits(prev => ({...prev, [keyConfig.env]: e.target.value}))}
                              placeholder="sk-..." className="h-8 text-xs flex-1 font-mono" />
                            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                              onClick={() => setShowKeys(prev => ({...prev, [keyConfig.env]: !prev[keyConfig.env]}))}>
                              {showKeys[keyConfig.env] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" className="h-8 text-xs shrink-0" disabled={savingKey === keyConfig.env}
                              onClick={() => handleSaveKey(keyConfig.env, keyEdits[keyConfig.env])}>
                              {savingKey === keyConfig.env ? <Loader2 className="w-3 h-3 animate-spin" /> : "Zapisz"}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 text-xs"
                              onClick={() => setKeyEdits(prev => ({...prev, [keyConfig.env]: undefined}))}>Anuluj</Button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 h-8 flex items-center px-3 rounded-md border bg-muted/50 text-xs text-muted-foreground font-mono">
                              {keyPreview ? `••••••••••••${keyPreview}` : "Brak klucza"}
                            </div>
                            <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 gap-1"
                              onClick={() => setKeyEdits(prev => ({...prev, [keyConfig.env]: ""}))}>
                              <Key className="w-3 h-3" /> {isAvailable ? "Zmien" : "Dodaj"}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Test button */}
                    <div className="shrink-0">
                      <Button variant="outline" size="sm" className="h-8 text-[10px] gap-1" disabled={!isAvailable || testingModel === keyConfig.models[0]}
                        onClick={() => handleTestModel(keyConfig.models[0])}>
                        {testingModel === keyConfig.models[0] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />}
                        Test
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Task routing info */}
      {isAdmin && aiConfig?.task_routing && (
        <Card className="rounded-xl border shadow-sm bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              Automatyczny routing AI
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground mb-3">
              System automatycznie wybiera najlepszy model do kazdego zadania na podstawie mocnych stron modeli.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries({
                "Generowanie artykulow": "article_generation",
                "Analiza SEO": "seo_analysis",
                "Generowanie cennika": "pricing_generation",
                "Trendy i newsy": "trending_topics",
                "Przepisywanie tresci": "article_rewriting",
                "Social media": "social_post",
                "Analiza stron": "site_analysis",
                "Odpowiedzi komentarze": "comment_reply",
                "Masowa generacja": "mass_generation",
              }).map(([label, taskType]) => {
                const models = aiConfig.task_routing[taskType] || [];
                const bestModel = models[0];
                const modelInfo = aiConfig.models?.[bestModel];
                return (
                  <div key={taskType} className="p-2 rounded-lg bg-muted/50">
                    <p className="text-[10px] text-muted-foreground">{label}</p>
                    <p className="text-xs font-semibold text-foreground">{modelInfo?.name || bestModel}</p>
                    <div className="flex gap-0.5 mt-0.5">
                      {models.slice(0, 3).map((m, i) => (
                        <span key={m} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-primary" : "bg-slate-300 dark:bg-slate-600"}`} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
